/**
 * Program: Germany
 * Language: English
 * Content: Index for all English translations
 */

import { flatten } from 'flat'
import Step1 from '@app/assets/images/step-1.jpg'
import Step2 from '@app/assets/images/step-2.jpg'
import Step3 from '@app/assets/images/step-3.jpg'
import ElgibiliyImg from '@app/assets/images/faq-topic-1.png'
import TradeInImg from '@app/assets/images/faq-topic-2.png'
import ShippingImg from '@app/assets/images/faq-topic-3.png'
import PaymentImg from '@app/assets/images/faq-topic-4.png'
import FaqImg from '@app/assets/images/faq.png'
import privacy from './privacy'
import terms from './terms'
import { LangProps } from '../../types'

const en: LangProps = flatten({
  /** -------------------------------------------------------------------------- *
   * Document Titles
   * -------------------------------------------------------------------------- */
  document_title_suffix: 'Dyson Trade In with Alchemy',
  document_title_home: 'Get started',
  document_title_faq: 'FAQs',
  document_title_quiz: 'Quiz',
  document_title_privacy: 'Privacy Policy',
  document_title_terms: 'Terms & Conditions',
  document_title_cookies: 'Cookies Policy',
  document_title_backoffice: 'Backoffice',
  document_title_signOut: 'Signing Out',
  document_title_notFound: 'Page not found',
  /** -------------------------------------------------------------------------- *
   * Landing Page
   * -------------------------------------------------------------------------- */
  landing_page: {
    content: {
      document_data: {
        title: '',
        description: '',
      },
      banner: {
        title: 'Trade in your old product, save on your new Dyson',
        pre_title: 'Dyson Trade-In',
        intro:
          "With Dyson Trade-In we'll give you up to €200 back when you purchase an eligible product and trade in your old one.",
        button_text: 'Begin my trade-in',
      },
      step_list: {
        title: '3 easy steps to Trade-In',
        small_print:
          '¹ At Dyson online, Dyson demo stores and selected partners. Cashback amount depends on the device bought and the device sent in.',
        button_text: 'Begin my trade-in',
        steps: [
          {
            content:
              "### Find out if you qualify\n\nTake our quick questionnaire and tell us about the product you would like to trade in, and we'll tell you how much we'll pay you for it.\n\nTo qualify, you must have purchased a Gen5detect™ Absolute, V15s Detect Submarine™, V16 Piston Animal, V16 Piston Animal Submarine™, Airwrap Co-anda2x™ or selected Airwrap i.d.™ models between September 15th and October 31st 2025.",
            image: Step1,
          },
          {
            content:
              '### Send your old product to us\n\nWe will provide you with a free pre-paid postage label that you can use to send your trade in product to us.\n\nSimply package your old product, attach the label, and drop it off to your nearest DHL drop-off point.\n\nRemember: You need to trade in a Floorcare product to qualify for trade-in cashback on your new Dyson vacuum (Dyson V15 Detect Submarine™, Gen5detect™ Absolute, V16 Piston Animal, V16 Piston Animal Submarine™) and a Beauty product to qualify for a trade-in cashback on your new Dyson Airwrap i.d.™ (selected models), Airwrap Co-anda2x™.',
            image: Step2,
          },
          {
            content:
              '### Get paid!\n\nAfter we receive your old product at our facility, we will issue your payment.\n\nPayments will be made between 30 and 45 days after receipt and will be paid into the account you provide.',
            image: Step3,
          },
        ],
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Closed Page
   * -------------------------------------------------------------------------- */
  closed_page: {
    content: {
      document_data: {
        title: '',
        description: '',
      },
      banner: {
        title: 'Thank you for your interest',
        intro: 'Unfortunately the current trade-in program is now closed',
        outro:
          'If you have already completed your trade-in application, please ensure your trade-in product has been sent to us within 2 weeks. Please refer to our email for instructions.',
        image: '',
        button_text: 'Visit Dyson Store',
        button_href: 'https://www.dyson.de/',
        button_type: 'absolute',
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Quiz: Customer Page
   * -------------------------------------------------------------------------- */
  quiz_customer_page: {
    content: {
      document_data: {
        title: 'Quiz',
        description: '',
      },
      base: {
        title: 'Your details',
        intro:
          'We will contact you to tell you when we have received your trade in product and processed your payment.',
        button_text: 'Continue',
      },
      questions: {
        customer_name: {
          label: 'What is your full name?',
          placeholder: 'Full name',
          validation_message: 'Please enter both your first and last name',
        },
        customer_email: {
          label: 'What is your email address?',
          placeholder: 'yourname@example.de',
          validation_message: 'Please enter your email address',
        },
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Quiz: Purchase Page
   * -------------------------------------------------------------------------- */
  quiz_purchased_product_page: {
    content: {
      document_data: {
        title: 'Quiz',
        description: '',
      },
      base: {
        title: 'Verify your recent purchase',
        intro: 'We can only accept a trade in product if you have purchased an eligible product from Dyson.',
        button_text: 'Continue',
      },
      questions: {
        purchased_model: {
          label: 'Which model did you purchase?',
          validation_message: '',
        },
        serial_number_file_upload: {
          label: 'Upload image of serial number',
          placeholder: 'Upload an image • Max 10MB',
          validation_message: 'Please upload a file',
        },
        serial_number: {
          label: 'Serial number',
          placeholder: '000-00-00000000',
          validation_message: 'Please enter a serial number',
        },
        receipt_file_upload: {
          label: 'Upload image of receipt',
          placeholder: 'Upload an image • Max 10MB',
          validation_message: 'Please upload a file',
        },
        date_of_purchase: {
          label: 'Date of purchase',
          placeholder: 'DD / MM / YYYY',
          validation_message: 'Please enter a date',
        },
        place_of_purchase: {
          label: 'Retailer',
          placeholder: 'Please select',
          validation_message: 'Please select a retailer',
        },
      },
      serial_number_dialog: {
        open_dialog: 'Where can I find it?',
        close_dialog: 'Dismiss',
        items: [
          {
            id: 1,
            content: `### On the underside of the battery pack\n\nThe serial number is printed on the back of the battery pack at the base of the handle`,
            image: '',
          },
          {
            id: 2,
            content: `### Behind the clear bin\n\nPlease look on the underside of the clear bin opposite the trigger to reveal the serial number`,
            image: '',
          },
          {
            id: 3,
            content: `### Inside the handle\n\nRemove the click-in battery to reveal the serial number inside the machine's handle`,
            image: '',
          },
        ],
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Quiz: Trade-in Page
   * -------------------------------------------------------------------------- */
  quiz_traded_product_page: {
    content: {
      document_data: {
        title: 'Quiz',
        description: '',
      },
      base: {
        title: 'Your trade in product',
        intro: 'Tell us about the product you want to trade in and we will tell you its value.',
        button_text: 'Check value',
      },
      questions: {
        traded_product_manufacturer: {
          question: 'Who is the manufacturer?',
        },
        traded_product_model: {
          label: '',
          validation_message: '',
        },
        agree_to_terms: {
          question: 'Confirmation of the Dyson Trade-In Terms',
          checkbox_label: 'I agree that my trade in meets the [conditions of the program]({terms_url})',
          terms_summary:
            '- My trade-in product includes the main body and motor.\n\n- My trade-in product is a regular full-sized corded or battery powered unit.\n\n- I am only trading a single eligible product.',
          input_error: '',
        },
      },
      offer_dialog: {
        title: 'Your product is worth {value}',
        description:
          'We will pay you after we have received your trade-in product at our facility and our technicians verify it is as you have described.\n\nIf there are any discrepancies between what you describe and what we receive, the valuation could be affected.',
        button_text: 'Continue',
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Quiz: Shipping Page
   * -------------------------------------------------------------------------- */
  quiz_address_page: {
    content: {
      document_data: {
        title: 'Quiz',
        description: '',
      },
      base: {
        title: 'Contact address',
        intro: 'We need to know your address to create your free postage label.',
        button_text: 'Continue',
      },
      questions: {
        lookup_address: {
          question: 'Lookup your address',
          placeholder: 'Start typing an address',
          manual_address_prompt: "Don't see your address? Add it manually.",
        },
        manual_enter_address: {
          street_address_label: 'Street address',
          city_label: 'City',
          postal_code_label: 'Post code',
          country_label: 'Country',
        },
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Quiz: Payment Page
   * -------------------------------------------------------------------------- */
  quiz_payment_page: {
    content: {
      document_data: {
        title: 'Quiz',
        description: '',
      },
      base: {
        title: 'Payment details',
        intro:
          'Please provide bank account details for where you would like us to send the payment. Please check this information carefully as we will not be liable for any mistakes',
        button_text: 'Continue',
      },
      questions: {
        account_holder: {
          label: 'Account holder',
          placeholder: 'Full name of account holder',
          validation_message: 'Please enter the account holder name',
        },
        iban: {
          label: 'IBAN',
          placeholder: 'e.g. DE00 0000 0000 0000 0000 00',
          validation_message: 'Please enter your IBAN',
        },
        agree_to_terms: {
          checkbox_label:
            'I have read and accept the [Terms and Conditions]({terms_url}) and [Privacy Policy]({privacy_url})',
        },
      },
      confirmation_dialog: {
        title: 'Confirm payment details',
        intro: 'Please make sure the payment details you have entered are correct.',
        cancel_button: 'Back',
        confirm_button: 'Confirm',
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Order Confirmation Page
   * -------------------------------------------------------------------------- */
  order_confirmation_page: {
    content: {
      document_data: {
        title: 'Quiz',
        description: '',
      },
      banner: {
        title: 'Thank you, your trade-in has been created!',
      },
      step_list: {
        title: 'What are the next steps?',
        steps: [
          {
            content:
              "#### Sit tight while we verify your details\n\nWe need to do some final checks on the details you've given us - but don't worry, this is expected and should take no longer than 1 working day.\n\nIf for some reason we need more information to complete our checks, we'll contact you by email.",
          },
          {
            content:
              '#### Pack your trade-in product and post it\n\nWe will email your free shipping label to the email you provided.\n\nWhen you receive it, please pack your trade-in product into a suitable box and attach your free pre-paid shipping label to it. Instructions for shipping will be included in the email.',
          },
          {
            content:
              '#### Get paid!\n\nWe will inspect your product when we receive it and if it as described we will make a payment to the account provided:',
            footnote: 'The payment will take 30-45 working days after we receive your product.',
          },
        ],
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Partial: FAQ Section
   * -------------------------------------------------------------------------- */
  faq_banner: {
    content: {
      title: 'Do you have any questions?',
      image: FaqImg,
      button_text: 'See our FAQs',
    },
  },
  /** -------------------------------------------------------------------------- *
   * Partial: Quiz Tracker
   * -------------------------------------------------------------------------- */
  quiz_tracker: {
    content: {
      purchase_step: {
        long_name: 'Verify your recent purchase',
        short_name: 'Verify purchase',
      },
      valuation_step: {
        long_name: 'Your trade in product',
        short_name: 'Trade-in valuation',
      },
      address_step: {
        long_name: 'Contact address',
        short_name: 'Contact address',
      },
      payment_step: {
        long_name: 'Payment details',
        short_name: 'Payment details',
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * FAQ Page
   * -------------------------------------------------------------------------- */
  faq_page: {
    content: {
      document_data: {
        title: '',
        description: '',
      },
      search: {
        title: 'FAQs',
        placeholder_text: 'Type to search',
      },
      topic_list: {
        title: 'Browse topics',
        topics: [
          {
            id: 1,
            title: 'Completing the application',
            icon: ElgibiliyImg,
            questions: [
              {
                question: 'Where do I go to complete my Dyson trade-in application?',
                answer: 'Visit the Dyson website to complete your Dyson trade-in application.',
              },
              {
                question: 'What are the qualifying products?',
                answer:
                  'You need to purchase one of these products to qualify for the trade-in event: V15s Detect Submarine™, Gen5detect™ Absolute, V16 Piston , V16 Piston Animal Submarine™, Airwrap Co-anda2x™, Airwrap i.d.™ (selected models).\n\nYour trade-in cashback will vary based on the product you purchased:\n- Gen5detect™ Absolute, V15s Detect Submarine™: 200 Euro for any traded-in brand (Dyson or non-Dyson)\n- V16 Piston Animal, V16 Piston Animal Submarine™: 150 Euro for any traded-in brand (Dyson or non-Dyson)\n- Dyson Airwrap i.d.™ (selected models), Airwrap Co-anda2x™: 100 Euro for any Dyson traded product, 75 Euro for any non-Dyson\n\nYou can only trade in an old Floorcare product to get cashback on a new Dyson Floorcare product, and an old Beauty product for a new Dyson Beauty product.',
              },
              {
                question: 'When does the trade-in event run?',
                answer:
                  'You must purchase a qualifying product between September 15th and October 31st, both inclusive.\n\nClaims must be made via the trade-in portal within 14 days from the date of purchase.\n\nAll traded in products must be sent to us within 14 days of claim application.',
              },
              {
                question: 'Who are the participating retailers?',
                answer:
                  'You must have purchased your new Dyson product from one of the below channels to be eligible for the trade-in program:\n\n1. From the Dyson website\n\n2. From a Dyson store (Austria: SCS (Vösendorf), Steffl (Wien), PlusCity (Pasching), Designer Outlet (Parndorf). Germany: Mönckebergstraße (Hamburg), Westfield Centro (Oberhausen), Tauentzienstraße (Berlin), Hohe Straße (Köln), MyZeil (Frankfurt am Main), Olympia (Einkaufszentrum))\n\n3. Any indirect Dyson retail partner from this list: Austria (Electronnic Partner, Expert, Red Zac, Metro, E4you, Mediamarkt & Saturn, Baslerbeauty, Kastner & Öhler, 0815, Otto); Germany (Mediamarkt & Saturn, Breuninger, Expert, Euronics, Metro, QVC, KaDeWe, Otto, Flaconi)',
              },
              {
                question: 'What are the terms of the trade-in event?',
                answer:
                  'When you purchase a qualifying Dyson product and trade in an old product, you will receive a cash payment to your nominated bank account.\n\nYou can read the full terms and conditions in the footer of this webpage.',
              },
              {
                question: 'Where do I need to live to participate in this program?',
                answer:
                  'You need to be able to drop off the device you want to trade in at one of the designated DHL collection points in Germany or Austria and have a bank account in Germany or Austria (depending on the country you are applying from) or another EEA country to participate in the trade-in event.',
              },
              {
                question: 'What details do I need to complete the form?',
                answer:
                  'You will need the name and serial number of your new Dyson product, your purchase receipt, the details of what product you are trading in, and your details, including your bank account number, to receive payment.',
              },
              {
                question: 'What if I have lost the purchase receipt for my new Dyson?',
                answer:
                  'You will need to request a new receipt from your retailer. Without the receipt we will not be able to process your trade-in application.',
              },
              {
                question: 'Who manages the Dyson trade-in event?',
                answer:
                  'The trade-in event is managed on behalf of Dyson by [Alchemy](https://www.wearealchemy.com/), a global leader in the circular economy and trade-in marketplace.',
              },
              {
                question: 'What happens after I make my trade-in application?',
                answer:
                  'You will receive an email stating that your application has been received and informing you of your trade-in reference number.',
              },
              {
                question: 'How long will it take for my trade-in application to be approved?',
                answer:
                  'Your application will be reviewed and a decision made on approval within 1 working day (Mon-Fri).\n\nUpon approval of your application, you will be emailed with the approval and with a prepaid shipping label that must be printed and placed on the box of the product to be traded in.',
              },
              {
                question: "I didn't receive an email or my application was not approved, who can I contact?",
                answer:
                  'You can contact Alchemy’s customer service team at [dyson@wearealchemy](mailto:dyson@wearealchemy.com).com or via the chat function on the trade-in page on the Dyson website.',
              },
            ],
          },
          {
            id: 2,
            title: 'Packaging and shipping your old product',
            icon: TradeInImg,
            questions: [
              {
                question: 'How do I package my old product?',
                answer:
                  'You need to use a suitable box to send in your product; the product should be packed inside securely and without excess space in the box. Please try and use a box that is of the right size.\n\nYou will attach the prepaid shipping label securely to that box.',
              },
              {
                question: 'Can I use any type of box?',
                answer:
                  'Yes, you may use any suitable cardboard box. You can use the original box from the new product if you still have it, or any other suitable box.',
              },
              {
                question: 'What do I need to send in the box?',
                answer:
                  'If you are trading in a Floorcare product, it must be intact and whole and must not be broken into pieces. It must be a regular full sized, corded or battery powered vacuum cleaner. You must make sure that the dust collection bin or bag is empty. You do not need to send the wands, hoses, cables, batteries or any other accessories (they are not required, only the main unit).\n\nIf you are trading in a Beauty product, it must be intact and whole and must not be broken into pieces. It must be a standard full-sized product. You may trade in any type of hair tool—for example, a hair dryer, multi-styler, or curling iron—regardless of brand or price. You do not need to send the cables or any other accessories (they are not required, only the main unit).\n\nYou must trade in only one eligible product.',
              },
              {
                question: 'Shipment of batteries',
                answer:
                  'Please note - If the device has a battery and you wish to send it in, it must be intact and free from physical damage.\n\nDo not send any damaged batteries in the package.\n\nDo not use a hazardous good label – a single battery shipment does not require a hazardous goods label.',
              },
              {
                question: 'How do I prepare the package for shipment?',
                answer:
                  'Make sure that the box is sealed and secure. Please use the smallest box possible without taking up unnecessary space.\n\nPrint and securely attach the prepaid shipping label provided by us via email to the package.\n\nThere should NOT be a dangerous goods label on your box. If there is, please stick the prepaid shipping label on top of it, so none of the dangerous goods label is showing. You may be subject to a fine if this label is visible.\n\nYou must drop off the package to a DHL drop off center.',
              },
            ],
          },
          {
            id: 3,
            title: 'After I have traded in my product',
            icon: ShippingImg,
            questions: [
              {
                question: 'How will I know when my traded in product has been received?',
                answer:
                  'We will email you when we receive your product and confirm that we have it and that the claimed amount will be paid to the details you provided in your trade-in application.',
              },
              {
                question: 'How long will it take to confirm receipt?',
                answer:
                  'We will typically confirm receipt of the traded in product in our depot to you via email within 7 days of its shipment by you.',
              },
              {
                question: 'What happens in the event that the trade-in product is disputed?',
                answer:
                  'In the event that what you sent us does not match what you claimed on your trade-in application, our customer service team will get in touch with you immediately.',
              },
              {
                question: 'How long will it take for me to get my trade-in payment?',
                answer:
                  'After verification of your new qualifying Dyson purchase, and receipt of your trade-in product, we will send the payment to your nominated account between 30 days and 45 days after we have received your product.',
              },
            ],
          },
          {
            id: 4,
            title: 'General',
            icon: PaymentImg,
            questions: [
              {
                question: 'How do I get in touch with the trade-in event customer service team?',
                answer:
                  'If you have any queries about the trade-in event or your application, you can reach the Alchemy team at [dyson@wearealchemy.com](mailto:dyson@wearealchemy.com).',
              },
              {
                question: 'Customer service operating hours',
                answer:
                  'We are available weekdays from 9am -  5pm. We aim to respond to all queries within a maximum of one working day.',
              },
            ],
          },
        ],
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Terms and Conditions Page
   * -------------------------------------------------------------------------- */
  terms_and_conditions_page: {
    content: {
      document_data: {
        title: '',
        description: '',
      },
      page_settings: {
        title: 'General Terms and Conditions for the Dyson Trade In Service',
        body: terms,
      },
    },
  },
  /** -------------------------------------------------------------------------- *
   * Privacy Policy Page
   * -------------------------------------------------------------------------- */
  privacy_policy_page: {
    content: {
      document_data: {
        title: '',
        description: '',
      },
      page_settings: {
        title: 'Privacy Policy of Alchemy (UK and Europe)',
        body: privacy,
      },
    },
  },
})

export default en
